import type { Distance, Point, SpatialRectangle } from "@pdfking/math"
import { AddMargins, GetXY, Outline } from "@pdfking/math"
import { DeleteDuplicates, SortBy } from "@pdfking/tfunc"

import type { GrowData, GrowLeaf, ImpositionNode, ImpositionPage } from "#internal"

import { DistanceRound } from "../../../utils/DistanceRound"
import { LeafPages } from "../layout/LeafPages"
import { OwnToSheet } from "../layout/OwnToSheet"
import { PageCell } from "../layout/PageCell"

/** One piece of a grow where it landed: its box on the sheet, the room it
 * takes there with its bleed, the laid-out page it prints on, and the piece
 * it is — none on a page that is a whole grow laid out as one. */
export type GrowCell = {
  box: SpatialRectangle<Distance>
  room: SpatialRectangle<Distance>
  page: ImpositionPage
  leaf: GrowLeaf | undefined
}

const distinct = (values: Distance[]): Distance[] =>
  SortBy(DeleteDuplicates(values, DistanceRound), DistanceRound)

// A box's span along `axis`: where it starts and where it ends.
const span = (box: SpatialRectangle<Distance>, axis: keyof Point<unknown>): [Distance, Distance] =>
  axis === "x"
    ? [box.x, (box.x + box.width) as Distance]
    : [box.y, (box.y + box.height) as Distance]

// The edges along `axis` where one page ends and another, beside it, begins:
// there the two are folded together. Anywhere else a page's edge is cut.
const shared = (boxes: SpatialRectangle<Distance>[], axis: keyof Point<unknown>): Distance[] => {
  const across = axis === "x" ? "y" : "x"
  return distinct(
    boxes.flatMap(a => {
      const [, end] = span(a, axis)
      const [a1, a2] = span(a, across)
      return boxes.some(b => {
        const [b1, b2] = span(b, across)
        return (
          DistanceRound(span(b, axis)[0]) === DistanceRound(end) &&
          DistanceRound(a1) < DistanceRound(b2) &&
          DistanceRound(b1) < DistanceRound(a2)
        )
      })
        ? [end]
        : []
    })
  )
}

// The pages a collapsed grow would have had, where they would have landed:
// every piece with the bleed beside it as its room, the way a book's page
// carries it, carried onto the sheet the way the one page landed: turned, and
// turned over on a rear.
const expand = (page: ImpositionPage, grow: GrowData): GrowCell[] => {
  const to_sheet = OwnToSheet(page)
  return grow.leaves.map(leaf => ({
    box: to_sheet(leaf.box),
    room: to_sheet(AddMargins(leaf.box, leaf.padding)),
    page,
    leaf,
  }))
}

/**
 * Where a grow landed, read off the laid-out sheet: every mark of the grow is
 * drawn from this. Its pages — a book's as they were laid out, a collapsed
 * grow's one page expanded back into the pages it holds — the box round the
 * room they take, the line of every page edge, and of the edges two pages
 * share: the folds. The rest are cut.
 *
 * `undefined` for a grow with no page under it.
 */
export const GrowGuides = (
  node: ImpositionNode,
  grow: GrowData
):
  | {
      canvas: SpatialRectangle<Distance>
      guides: Point<Distance[]>
      folds: Point<Distance[]>
      cells: GrowCell[]
    }
  | undefined => {
  const pages = [...LeafPages(node)]
  const cells: GrowCell[] = grow.collapsed
    ? pages.flatMap(page => expand(page, grow))
    : pages.map(page => ({
        box: page.position,
        room: PageCell(page),
        page,
        leaf: page.data?.page?.leaf,
      }))
  if (!cells.length) return undefined
  const boxes = cells.map(cell => cell.box)
  const edges = boxes.map(GetXY)
  return {
    canvas: Outline(cells.map(cell => cell.room)),
    guides: {
      x: distinct(edges.flatMap(edge => [edge.x1, edge.x2])),
      y: distinct(edges.flatMap(edge => [edge.y1, edge.y2])),
    },
    folds: { x: shared(boxes, "x"), y: shared(boxes, "y") },
    cells,
  }
}
